import React, { Component } from 'react';
import { connect } from 'react-redux';

import Icon from 'components/atoms/Icon';
import { PLAY_BACK_SPEEDS, PLAYBACK_SPEED } from 'data/static';
import { BackgroundType, RootState } from 'types';
import { VoidInterface } from 'types/functions';
import { DateTimeFormat } from 'utils/dateTimeFormat';
import { checkDifferent } from 'utils/formHandlers';
import { twclsx } from 'utils/twclsx';

interface TimeControlProps {
    startTime?: DateTimeFormat;
    backgrounds: BackgroundType[];
    endTime?: DateTimeFormat;
    currentTime?: DateTimeFormat;
    stopTimer: VoidInterface;
    startTimer: VoidInterface;
    changeTime: (seconds: number) => void;
    changeSpeed: (speed: number) => void;
    openMenu?: boolean;
    openTool?: boolean;
    speed: number;
    play: boolean;
}

interface TimeControlState {
    currentTime?: DateTimeFormat;
    openMenu?: boolean;
    openTool?: boolean;
    speed: number;
    play: boolean;
    background: BackgroundType | null;
}

class TimeControl extends Component<TimeControlProps, TimeControlState> {
    sliderRef: React.RefObject<HTMLDivElement>; // Reference to the slider element

    constructor(props: TimeControlProps) {
        super(props);
        this.state = { background: null, openMenu: false, openTool: false, speed: PLAYBACK_SPEED, play: props.play };
        this.sliderRef = React.createRef();
    }

    static getDerivedStateFromProps(props: TimeControlProps, state: TimeControlState) {
        if (
            props.currentTime?.toTimestamp() != state.currentTime?.toTimestamp() ||
            props.openMenu !== state.openMenu ||
            props.openTool !== state.openTool ||
            props.play !== state.play ||
            props.speed !== state.speed
        ) {
            return {
                currentTime: props.currentTime,
                openMenu: props.openMenu,
                openTool: props.openTool,
                speed: props.speed,
                play: props.play,
            };
        }
        if (
            checkDifferent(
                state.background,
                props.backgrounds.find((x) => {
                    return x.status === 'active';
                })
            )
        ) {
            return {
                background: props.backgrounds.find((x) => {
                    return x.status === 'active';
                }),
            };
        }
        return null;
    }

    play = () => {
        this.props.startTimer();
    };

    stop = () => {
        this.props.stopTimer();
    };

    jumpForward = (time: number) => {
        this.props.changeTime(time);
    };

    jumpBackward = (time: number) => {
        this.props.changeTime(time * -1);
    };

    changeSpeed = () => {
        const currentIndex = PLAY_BACK_SPEEDS.indexOf(this.state.speed);
        const nextIndex = (currentIndex + 1) % PLAY_BACK_SPEEDS.length; // Loop back to first if last
        this.props.changeSpeed(PLAY_BACK_SPEEDS[nextIndex]);
    };

    handleSliderClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        const { startTime, endTime, currentTime } = this.props;
        if (!startTime || !endTime || !currentTime) {
            return;
        }
        if (this.sliderRef.current) {
            const sliderRect = this.sliderRef.current.getBoundingClientRect();
            const clickPosition = event.clientX - sliderRect.left; // Get click position relative to the slider
            const sliderWidth = sliderRect.width; // Total width of the slider

            const percentage = (clickPosition / sliderWidth) * 100;

            const totalDuration = endTime?.toTimestamp() - startTime?.toTimestamp(); // Calculate total duration in milliseconds

            const currentPercentage = ((currentTime?.toTimestamp() - startTime?.toTimestamp()) / totalDuration) * 100;
            const percentageOffset = percentage - currentPercentage;
            const timeOffset = (percentageOffset / 100) * totalDuration;
            const timeDifference = Math.round(timeOffset / 1000);

            this.props.changeTime(timeDifference);
        }
    };

    render() {
        return (
            <div
                className={twclsx(
                    'bottom-6',
                    'fixed',
                    this.state.openMenu
                        ? 'ml-menu w-main'
                        : this.state.openTool
                        ? 'ml-28 w-fullPage'
                        : 'ml-14 w-fullPage',
                    'left-0',
                    'z-10',
                    'flex',
                    'margin-transition',

                    'pl-2',
                    'pr-3',
                    'rounded-md',
                    'no-select',

                    this.state.background?.text === 'dark' ? 'text-black' : 'text-white'
                )}
            >
                <div className="flex gap-2 w-full justify-between rounded-md bg-black bg-opacity-15 pt-4">
                    <div className="w-30 ">
                        <div className="flex gap-2 self-center">
                            {this.state.play ? (
                                <div onClick={this.stop}>
                                    <Icon
                                        className={twclsx(
                                            'cursor-pointer',
                                            'w-5',
                                            'h-5',
                                            this.state.background?.text === 'dark' ? 'text-black' : 'text-white'
                                        )}
                                    >
                                        <path d="M6 5m0 1a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v12a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1z"></path>
                                        <path d="M14 5m0 1a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v12a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1z"></path>
                                    </Icon>
                                </div>
                            ) : (
                                <div onClick={this.play}>
                                    <Icon
                                        className={twclsx(
                                            'cursor-pointer',
                                            'w-5',
                                            'h-5',
                                            this.state.background?.text === 'dark' ? 'text-black' : 'text-white'
                                        )}
                                    >
                                        <path d="M7 4v16l13 -8z"></path>
                                    </Icon>
                                </div>
                            )}

                            <div
                                onClick={() => {
                                    this.jumpBackward(10);
                                }}
                                className="relative"
                            >
                                <Icon
                                    className={twclsx(
                                        'cursor-pointer',
                                        'w-5',
                                        'h-5',
                                        this.state.background?.text === 'dark' ? 'text-black' : 'text-white'
                                    )}
                                >
                                    <path d="M14 6l-6 6l6 6v-12" />
                                </Icon>
                            </div>

                            <div
                                onClick={() => {
                                    this.jumpBackward(60);
                                }}
                                className="relative"
                            >
                                <Icon
                                    className={twclsx(
                                        'cursor-pointer',
                                        'w-5',
                                        'h-5',
                                        this.state.background?.text === 'dark' ? 'text-black' : 'text-white'
                                    )}
                                >
                                    <path d="M21 5v14l-8 -7z"></path>
                                    <path d="M10 5v14l-8 -7z"></path>
                                </Icon>
                                <div className="absolute text-xs top-3 right-1">-1</div>
                            </div>
                            <div
                                onClick={() => {
                                    this.jumpBackward(600);
                                }}
                                className="relative"
                            >
                                <Icon
                                    className={twclsx(
                                        'cursor-pointer',
                                        'w-5',
                                        'h-5',
                                        this.state.background?.text === 'dark' ? 'text-black' : 'text-white'
                                    )}
                                >
                                    <path d="M21 5v14l-8 -7z"></path>
                                    <path d="M10 5v14l-8 -7z"></path>
                                </Icon>
                                <div className="absolute text-xs top-3 right-1">-10</div>
                            </div>

                            <div onClick={this.changeSpeed} className="cursor-pointer text-lg">
                                X{this.state.speed}
                            </div>
                        </div>
                    </div>
                    <div
                        onClick={this.handleSliderClick}
                        ref={this.sliderRef}
                        className="flex flex-col grow cursor-pointer  "
                    >
                        <div
                            className={twclsx(
                                this.state.background?.text === 'dark' ? 'bg-gray-100' : 'bg-white',
                                'w-full',
                                'shadow-md',
                                'h-3',
                                'my-1',
                                'relative'
                            )}
                        >
                            <div
                                style={{
                                    left: `${
                                        //@ts-ignore
                                        ((this.state?.currentTime?.toTimestamp() -
                                            //@ts-ignore
                                            this.props?.startTime?.toTimestamp()) /
                                            //@ts-ignore
                                            (this.props?.endTime?.toTimestamp() -
                                                //@ts-ignore
                                                this.props?.startTime?.toTimestamp())) *
                                        100
                                    }%`,
                                }}
                                className={twclsx(
                                    this.state.background?.text === 'dark' ? 'text-black' : 'text-white',
                                    '-ml-3',
                                    'text-sm',
                                    'absolute',
                                    'bottom-2',
                                    'pb-1'
                                )}
                            >
                                {this.props.currentTime?.formatDateTimeToString('HH:mm')}
                            </div>

                            <div
                                style={{
                                    width: `${
                                        //@ts-ignore
                                        ((this.state?.currentTime?.toTimestamp() -
                                            //@ts-ignore
                                            this.props?.startTime?.toTimestamp()) /
                                            //@ts-ignore
                                            (this.props?.endTime?.toTimestamp() -
                                                //@ts-ignore
                                                this.props?.startTime?.toTimestamp())) *
                                        100
                                    }%`,
                                }}
                                className="absolute left-0 h-full bg-gray-300 active-time"
                            ></div>

                            <div className="my-1 h-full relative">
                                {[...Array(21)].map((_, index) => {
                                    const left = `${index * 5}%`;
                                    return (
                                        <div
                                            key={index}
                                            style={{ left }}
                                            className="absolute top-0 h-1 border-l border-gray-600"
                                        ></div>
                                    );
                                })}
                            </div>
                        </div>
                        <div
                            className={twclsx(
                                this.state.background?.text === 'dark' ? 'text-black' : 'text-white',
                                'h-3',
                                'text-sm',
                                'leading-3',
                                'flex',
                                'justify-between'
                            )}
                        >
                            <div>{this.props.startTime?.formatDateTimeToString('HH:mm Z')}</div>
                            <div>{this.props.endTime?.formatDateTimeToString('HH:mm Z')}</div>
                        </div>
                        <div
                            className={twclsx(
                                this.state.background?.text === 'dark' ? 'text-black' : 'text-white',
                                'h-3',
                                'text-sm',
                                'leading-3',
                                'flex',
                                'justify-between'
                            )}
                        >
                            <div>{this.props.startTime?.formatDateTimeToString()}</div>
                            <div>{this.props.endTime?.formatDateTimeToString()}</div>
                        </div>
                    </div>
                    <div className="grow-0">
                        <div
                            onClick={() => {
                                this.jumpForward(600);
                            }}
                            className="relative"
                        >
                            <Icon
                                className={twclsx(
                                    'cursor-pointer',
                                    'w-5',
                                    'h-5',
                                    this.state.background?.text === 'dark' ? 'text-black' : 'text-white'
                                )}
                            >
                                <path d="M3 5v14l8 -7z"></path>
                                <path d="M14 5v14l8 -7z"></path>
                            </Icon>
                            <div className="absolute text-xs top-3 left-2">10</div>
                        </div>
                    </div>
                    <div className="grow-0">
                        <div
                            onClick={() => {
                                this.jumpForward(60);
                            }}
                            className="relative"
                        >
                            <Icon
                                className={twclsx(
                                    'cursor-pointer',
                                    'w-5',
                                    'h-5',
                                    this.state.background?.text === 'dark' ? 'text-black' : 'text-white'
                                )}
                            >
                                <path d="M3 5v14l8 -7z"></path>
                                <path d="M14 5v14l8 -7z"></path>
                            </Icon>
                            <div className="absolute text-xs top-3 left-2">1</div>
                        </div>
                    </div>
                    <div className="grow-0">
                        <div
                            onClick={() => {
                                this.jumpForward(10);
                            }}
                            className="relative"
                        >
                            <Icon
                                className={twclsx(
                                    'cursor-pointer',
                                    'w-5',
                                    'h-5',
                                    this.state.background?.text === 'dark' ? 'text-black' : 'text-white'
                                )}
                            >
                                <path d="M10 18l6 -6l-6 -6v12" />
                            </Icon>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const timeControlStateToProps = (state: RootState) => ({
    map: state.map.map,
    openMenu: state.layout.layout.openMenu,
    openTool: state.layout.layout.openTool,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const timeControlDispatchToProps = () => ({});

export default connect(timeControlStateToProps, timeControlDispatchToProps)(TimeControl);
